import { useAccount, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { AccessPolicy, RawDatasource } from "src/db";
import { getAPI } from "src/utils/fetch";
import { useDatasourceOverview } from "./useDatasources";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

interface UserInfo {
    id: string;
    org_code: string;
    email: string;
    roles: string[];
    user_preferences: UserPreferences;
}

interface Message {
    content: string;
    level: string;
    id: string;
}

export interface Datasource {
    id: string;
    access_policy: AccessPolicy;
    // Whether the user can currently use this datasource (docs/search, viewer,
    // approval gate). For role datasources this is a plain role-membership check. For
    // "always_on" datasources it's additionally gated server-side on product-line
    // overlap with the user's role datasources. Code that needs "is this a real role
    // grant" (approval gate, preferences, role sync) should check
    // `access_policy !== "always_on" && available` rather than a separate has_access
    // flag - the two are identical for role datasources, and has_access would always
    // be false for always_on ones anyway.
    available: boolean;
}

export const isRoleGranted = (ds?: Pick<Datasource, "access_policy" | "available">) =>
    !!ds && ds.access_policy !== "always_on" && ds.available;

export interface UserDatasourceDetail {
    id: string;
    has_access: boolean;
    access_policy: AccessPolicy;
    name: string;
    description: string;
    ingestion_status: string;
    product_line_filter: string[];
}

export interface AccessRequest {
    id: string;
    user_id: string;
    user_email: string;
    requested_datasources: { id: string }[];
    status: string;
    reason: string;
    request_datetime?: number;
    last_updated?: number;
}

export interface UserResponse {
    info: UserInfo;
    messages: Message[];
    datasources: Datasource[];
    access_requests: AccessRequest[];
}

export type UserPreferences = {
    datasources: string[];
    product_line: string;
    memory_enabled: boolean;
};

export const useUser = (options?: { forceRefetchOnMount?: boolean }) => {
    const { instance } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<UserResponse, Error>({
        queryKey: ["user"],
        queryFn: async () => {
            const response = await getAPI({
                url: `${BACKEND_URL}/v1/user`,
                instance,
            });
            const data = await response.json();
            return data;
        },
        enabled: isAuthenticated,
        refetchOnWindowFocus: false,
        // "always" forces a single fresh fetch on mount (even with cached data
        // from another page) instead of the default of skipping it when data is
        // already cached - used by pages that must reflect the latest
        // permissions/preferences as soon as they open (e.g. the preferences page).
        refetchOnMount: options?.forceRefetchOnMount ? "always" : false,
        refetchOnReconnect: false,
    });
};

/**
 * Merges the user's datasource access list (id + availability, from /v1/user) with
 * datasource labels and product lines from /v1/system/configuration, since the latter
 * is the single source of truth for datasource details. Always-on datasources are
 * excluded - they're never a role grant, so they don't belong in a "your datasources"
 * list (preferences, request-access flow). Synced datasources (e.g. MGT Product
 * Support Archive, access managed by an external process) ARE included regardless of
 * availability, so they're visible in preferences/datasource details even without
 * access - but callers must consult `access_policy` to suppress any request-access
 * affordance for them, since that flow can never grant a synced datasource.
 *
 * UserDatasourceDetail.has_access here means "role-granted", which for this
 * always-on-excluded set is exactly what `available` already means.
 */
export const useUserDatasources = () => {
    const { data: user, isLoading: isUserLoading, isSuccess: isUserSuccess } = useUser();
    const {
        data: overview,
        isLoading: isOverviewLoading,
        isSuccess: isOverviewSuccess,
    } = useDatasourceOverview();

    const data = useMemo<UserDatasourceDetail[] | undefined>(() => {
        if (!user?.datasources) return undefined;
        const overviewById = new Map((overview ?? []).map((ds) => [ds.id, ds]));

        return user.datasources
            .filter((ds) => ds.access_policy !== "always_on")
            .map((ds) => {
                const details = overviewById.get(ds.id);
                return {
                    id: ds.id,
                    has_access: ds.available,
                    access_policy: ds.access_policy,
                    name: details?.label ?? ds.id,
                    description: details?.description ?? "",
                    ingestion_status: details?.ingestion_status ?? "",
                    product_line_filter: details?.product_lines ?? [],
                };
            });
    }, [user, overview]);

    return {
        data,
        isLoading: isUserLoading || isOverviewLoading,
        isSuccess: isUserSuccess && isOverviewSuccess,
    };
};

/**
 * Same as useDatasourceOverview, but with access_policy/available overridden from the
 * user's actual access list (/v1/user), since /v1/system/configuration doesn't carry
 * either at all.
 */
export const useDatasourceOverviewWithAccess = () => {
    const { data: user, isLoading: isUserLoading, isSuccess: isUserSuccess } = useUser();
    const {
        data: overview,
        isLoading: isOverviewLoading,
        isSuccess: isOverviewSuccess,
    } = useDatasourceOverview();

    const data = useMemo<RawDatasource[] | undefined>(() => {
        if (!overview) return undefined;
        const userDatasourceById = new Map((user?.datasources ?? []).map((ds) => [ds.id, ds]));

        return overview.map((ds) => {
            const userDatasource = userDatasourceById.get(ds.id);
            return {
                ...ds,
                access_policy: userDatasource?.access_policy,
                available: userDatasource?.available ?? false,
            };
        });
    }, [overview, user]);

    return {
        data,
        isLoading: isUserLoading || isOverviewLoading,
        isSuccess: isUserSuccess && isOverviewSuccess,
    };
};

/**
 * IDs of "always_on" datasources the user can currently use, i.e. they already have a
 * role-granted datasource sharing a product line with it (see backend
 * AuthorizationService.evaluate_datasource_access). Used to surface always-on
 * datasources in the Documents Viewer without treating them as a role grant.
 */
export const useAvailableAlwaysOnDatasourceIds = (): Set<string> => {
    const { data: user } = useUser();

    return useMemo(() => {
        const ids = (user?.datasources ?? [])
            .filter((ds) => ds.access_policy === "always_on" && ds.available)
            .map((ds) => ds.id);
        return new Set(ids);
    }, [user]);
};
